import { useEffect, useState } from 'react';
import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import DashboardLayout from '@/components/layout/DashboardLayout';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Trash2, Search, Filter, Check, Eye, Calendar as CalendarIcon, Euro, Download, FileSpreadsheet, FileText, ChevronDown, X, Globe, Tag, Building2, FileDown, Clock, Lock, Sparkles, Brain, Plus, Loader2 } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { Skeleton } from '@/components/ui/skeleton';
import { formatDateLocalized, formatRelativeTime } from '@/hooks/useLocationDetection';
import { cn } from '@/lib/utils';
import CpvSearch from '@/components/CpvSearch';
import CpvBadge from '@/components/CpvBadge';
import { getTedPdfUrl } from '@/lib/tedUtils';
import { useSubscription } from '@/hooks/useSubscription';

interface TedResult {
  id: string;
  ted_notice_id: string;
  title: string;
  description: string | null;
  buyer_name: string | null;
  country: string | null;
  cpv_codes: string[] | null;
  estimated_value: number | null;
  currency: string | null;
  publication_date: string | null;
  deadline_date: string | null;
  ted_url: string | null;
  is_read: boolean;
  created_at: string;
}

export default function Results() {
  const [results, setResults] = useState<TedResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRead, setFilterRead] = useState<'all' | 'read' | 'unread'>('all');
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [dateFrom, setDateFrom] = useState<Date | undefined>();
  const [dateTo, setDateTo] = useState<Date | undefined>();
  const [minValue, setMinValue] = useState('');
  const [maxValue, setMaxValue] = useState('');
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [selectedCpvCodes, setSelectedCpvCodes] = useState<string[]>([]);
  const [selectedBuyers, setSelectedBuyers] = useState<string[]>([]);
  const [buyerSearchQuery, setBuyerSearchQuery] = useState('');
  const [includeNoValue, setIncludeNoValue] = useState(true);
  // Selection for analysis
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [showAnalysisDialog, setShowAnalysisDialog] = useState(false);
  const [datasets, setDatasets] = useState<{ id: string; name: string }[]>([]);
  const [selectedDatasetId, setSelectedDatasetId] = useState('');
  const [newDatasetName, setNewDatasetName] = useState('');
  const [sendingToAnalysis, setSendingToAnalysis] = useState(false);
  const { toast } = useToast();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { isSubscribed, isAdmin } = useSubscription();
  
  // Free tier limitations
  const isFreeUser = !isSubscribed && !isAdmin;
  const FREE_RESULTS_LIMIT = 3;

  const clearAdvancedFilters = () => {
    setDateFrom(undefined);
    setDateTo(undefined);
    setMinValue('');
    setMaxValue('');
    setSelectedCountries([]);
    setSelectedCpvCodes([]);
    setSelectedBuyers([]);
    setBuyerSearchQuery('');
    setIncludeNoValue(true);
  };

  const hasAdvancedFilters = dateFrom || dateTo || minValue || maxValue || selectedCountries.length > 0 || selectedCpvCodes.length > 0 || selectedBuyers.length > 0;

  // Get unique countries from results
  const availableCountries = [...new Set(results.map(r => r.country).filter(Boolean))] as string[];

  // Get unique CPV codes from results
  const availableCpvCodes = [...new Set(results.flatMap(r => r.cpv_codes || []).filter(Boolean))].sort();

  // Get unique buyers from results
  const availableBuyers = [...new Set(results.map(r => r.buyer_name).filter(Boolean))] as string[];
  
  // Filter buyers based on search
  const filteredBuyers = buyerSearchQuery 
    ? availableBuyers.filter(buyer => buyer.toLowerCase().includes(buyerSearchQuery.toLowerCase()))
    : availableBuyers.slice(0, 15);

  const prepareExportData = (data: TedResult[]) => {
    return data.map(result => ({
      [t('resultsPage.exportHeaders.tenderId')]: result.ted_notice_id,
      [t('resultsPage.exportHeaders.title')]: result.title,
      [t('resultsPage.exportHeaders.description')]: result.description || '',
      [t('resultsPage.exportHeaders.buyer')]: result.buyer_name || '',
      [t('resultsPage.exportHeaders.country')]: result.country || '',
      [t('resultsPage.exportHeaders.cpvCodes')]: result.cpv_codes?.join(', ') || '',
      [t('resultsPage.exportHeaders.estimatedValue')]: result.estimated_value || '',
      [t('resultsPage.exportHeaders.currency')]: result.currency || 'EUR',
      [t('resultsPage.exportHeaders.publicationDate')]: result.publication_date ? formatDateLocalized(result.publication_date) : '',
      [t('resultsPage.exportHeaders.deadline')]: result.deadline_date ? formatDateLocalized(result.deadline_date) : '',
      [t('resultsPage.exportHeaders.winner', { defaultValue: 'Vincitore' })]: (result as any).winner_name || '',
      [t('resultsPage.exportHeaders.winnerVat', { defaultValue: 'P.IVA Vincitore' })]: (result as any).winner_vat_number || '',
      [t('resultsPage.exportHeaders.tedLink')]: result.ted_url || '',
      [t('resultsPage.exportHeaders.status')]: result.is_read ? t('resultsPage.read') : t('resultsPage.unread'),
    }));
  };

  const exportToCSV = () => {
    const data = prepareExportData(filteredResults);
    if (data.length === 0) {
      toast({ variant: "destructive", title: t('resultsPage.noDataToExport') });
      return;
    }

    const headers = Object.keys(data[0]);
    const csvContent = [
      headers.join(';'),
      ...data.map(row => headers.map(h => `"${String(row[h as keyof typeof row]).replace(/"/g, '""')}"`).join(';'))
    ].join('\n');

    const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `ted_tenders_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);

    toast({ title: t('resultsPage.exportCompleted'), description: `${data.length} ${t('resultsPage.tendersExported')}` });
  };

  const exportToExcel = async () => {
    const data = prepareExportData(filteredResults);
    if (data.length === 0) {
      toast({ variant: "destructive", title: t('resultsPage.noDataToExport') });
      return;
    }

    try {
      const { exportRowsToExcel } = await import('@/lib/excel');
      await exportRowsToExcel(data, 'TED Tenders', `ted_tenders_${new Date().toISOString().split('T')[0]}.xlsx`);

      toast({ title: t('resultsPage.exportCompleted'), description: `${data.length} ${t('resultsPage.tendersExported')}` });
    } catch (error) {
      console.error('Export error:', error);
      toast({ variant: "destructive", title: t('resultsPage.error') });
    }
  };

  useEffect(() => {
    fetchResults();
  }, []);

  const fetchResults = async () => {
    try {
      const { data, error } = await supabase
        .from('ted_results')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setResults(data || []);
    } catch (error) {
      console.error('Error fetching results:', error);
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (id: string) => {
    try {
      const { error } = await supabase
        .from('ted_results')
        .update({ is_read: true })
        .eq('id', id);

      if (error) throw error;
      setResults(results.map(r => r.id === id ? { ...r, is_read: true } : r));
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('resultsPage.error'),
        description: error.message,
      });
    }
  };

  const deleteResult = async (id: string) => {
    try {
      const { error } = await supabase
        .from('ted_results')
        .delete()
        .eq('id', id);

      if (error) throw error;
      setResults(results.filter(r => r.id !== id));
      toast({ title: t('resultsPage.allDeleted') });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: "Errore",
        description: error.message,
      });
    }
  };

  const deleteAllResults = async () => {
    try {
      const idsToDelete = filteredResults.map(r => r.id);
      if (idsToDelete.length === 0) return;

      const { error } = await supabase
        .from('ted_results')
        .delete()
        .in('id', idsToDelete);

      if (error) throw error;
      setResults(results.filter(r => !idsToDelete.includes(r.id)));
      toast({ title: t('resultsPage.allDeleted'), description: t('resultsPage.tendersDeleted', { count: idsToDelete.length }) });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('resultsPage.error'),
        description: error.message,
      });
    }
  };

  const markAllAsRead = async () => {
    try {
      const { error } = await supabase
        .from('ted_results')
        .update({ is_read: true })
        .eq('is_read', false);

      if (error) throw error;
      setResults(results.map(r => ({ ...r, is_read: true })));
      toast({ title: t('resultsPage.allMarkedRead') });
    } catch (error: any) {
      toast({
        variant: "destructive",
        title: t('resultsPage.error'),
        description: error.message,
      });
    }
  };

  const toggleSelect = (id: string) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (selectedIds.size === displayedResults.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(displayedResults.map(r => r.id)));
    }
  };

  const fetchDatasets = async () => {
    const { data } = await supabase.from('analysis_datasets').select('id, name').order('created_at', { ascending: false });
    setDatasets(data || []);
    if (data && data.length > 0) setSelectedDatasetId(data[0].id);
  };

  const openAnalysisDialog = () => {
    if (selectedIds.size === 0) {
      toast({ variant: 'destructive', title: t('dataAnalysis.noSelection', 'Select at least one tender') });
      return;
    }
    fetchDatasets();
    setShowAnalysisDialog(true);
  };

  const sendToAnalysis = async () => {
    setSendingToAnalysis(true);
    try {
      let datasetId = selectedDatasetId;
      // Create new dataset if needed
      if (!datasetId && newDatasetName.trim()) {
        const { data: { user } } = await supabase.auth.getUser();
        if (!user) throw new Error('Not authenticated');
        const { data: newDs, error: dsErr } = await supabase
          .from('analysis_datasets')
          .insert({ name: newDatasetName.trim(), user_id: user.id })
          .select()
          .single();
        if (dsErr) throw dsErr;
        datasetId = newDs.id;
      }
      if (!datasetId) {
        toast({ variant: 'destructive', title: t('dataAnalysis.selectOrCreate', 'Select or create a dataset') });
        setSendingToAnalysis(false);
        return;
      }

      // Insert items (ignore duplicates)
      const items = Array.from(selectedIds).map(id => ({ dataset_id: datasetId, ted_result_id: id }));
      const { error } = await supabase.from('analysis_dataset_items').upsert(items, { onConflict: 'dataset_id,ted_result_id', ignoreDuplicates: true });
      if (error) throw error;

      toast({ title: t('dataAnalysis.sentToAnalysis', 'Sent to Analysis'), description: `${selectedIds.size} ${t('dataAnalysis.tenders', 'tenders')}` });
      setSelectedIds(new Set());
      setShowAnalysisDialog(false);
      setNewDatasetName('');
    } catch (error: any) {
      toast({ variant: 'destructive', title: 'Error', description: error.message });
    } finally {
      setSendingToAnalysis(false);
    }
  };

  const toggleCountry = (country: string) => {
    setSelectedCountries(prev => 
      prev.includes(country) 
        ? prev.filter(c => c !== country)
        : [...prev, country]
    );
  };

  const toggleBuyer = (buyer: string) => {
    setSelectedBuyers(prev => 
      prev.includes(buyer) 
        ? prev.filter(b => b !== buyer)
        : [...prev, buyer]
    );
  };
  const filteredResults = results.filter(r => {
    const matchesSearch = !searchQuery || 
      r.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      r.buyer_name?.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesFilter = filterRead === 'all' || 
      (filterRead === 'read' && r.is_read) ||
      (filterRead === 'unread' && !r.is_read);

    // Date filter
    let matchesDate = true;
    if (dateFrom && r.publication_date) {
      matchesDate = matchesDate && new Date(r.publication_date) >= dateFrom;
    }
    if (dateTo && r.publication_date) {
      matchesDate = matchesDate && new Date(r.publication_date) <= dateTo;
    }

    // Value filter
    let matchesValue = true;
    const hasValueFilter = minValue || maxValue;
    if (hasValueFilter) {
      if (!r.estimated_value) {
        matchesValue = includeNoValue;
      } else {
        if (minValue) {
          matchesValue = matchesValue && r.estimated_value >= parseFloat(minValue);
        }
        if (maxValue) {
          matchesValue = matchesValue && r.estimated_value <= parseFloat(maxValue);
        }
      }
    }

    // Country filter
    const matchesCountry = selectedCountries.length === 0 || 
      (r.country && selectedCountries.includes(r.country));

    // CPV filter
    const matchesCpv = selectedCpvCodes.length === 0 || 
      (r.cpv_codes && r.cpv_codes.some(cpv => selectedCpvCodes.includes(cpv)));

    // Buyer filter
    const matchesBuyer = selectedBuyers.length === 0 || 
      (r.buyer_name && selectedBuyers.includes(r.buyer_name));

    return matchesSearch && matchesFilter && matchesDate && matchesValue && matchesCountry && matchesCpv && matchesBuyer;
  });

  // Apply free tier limit
  const totalFilteredCount = filteredResults.length;
  const displayedResults = isFreeUser ? filteredResults.slice(0, FREE_RESULTS_LIMIT) : filteredResults;
  const hasMoreResults = isFreeUser && totalFilteredCount > FREE_RESULTS_LIMIT;

  const unreadCount = results.filter(r => !r.is_read).length;

  return (
    <DashboardLayout>
      <TooltipProvider>
      <div className="space-y-8 animate-fade-in">
        <div className="flex items-center justify-between flex-wrap gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground">{t('resultsPage.title')}</h1>
            <p className="text-muted-foreground mt-1">
              {t('resultsPage.subtitle')}
            </p>
          </div>
          <div className="flex items-center gap-2">
            {selectedIds.size > 0 && (
              <Button variant="default" onClick={openAnalysisDialog} className="gap-2">
                <Brain className="w-4 h-4" />
                {t('dataAnalysis.sendToAnalysis', 'Send to Analysis')} ({selectedIds.size})
              </Button>
            )}
            <DropdownMenu>
              <Tooltip>
                <TooltipTrigger asChild>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline">
                      <Download className="w-4 h-4 mr-2" />
                      {t('resultsPage.export')}
                    </Button>
                  </DropdownMenuTrigger>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.export')}</p>
                </TooltipContent>
              </Tooltip>
              <DropdownMenuContent align="end">
                <DropdownMenuItem onClick={exportToCSV}>
                  <FileText className="w-4 h-4 mr-2" />
                  {t('resultsPage.exportCSV')}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={exportToExcel}>
                  <FileSpreadsheet className="w-4 h-4 mr-2" />
                  {t('resultsPage.exportExcel')}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
            {unreadCount > 0 && (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button variant="outline" onClick={markAllAsRead}>
                    <Check className="w-4 h-4 mr-2" />
                    {t('resultsPage.markAllRead')} ({unreadCount})
                  </Button>
                </TooltipTrigger>
                <TooltipContent>
                  <p>{t('tooltips.markAllRead')}</p>
                </TooltipContent>
              </Tooltip>
            )}
            {filteredResults.length > 0 && (
              <AlertDialog>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <AlertDialogTrigger asChild>
                      <Button variant="destructive">
                        <Trash2 className="w-4 h-4 mr-2" />
                        {t('resultsPage.deleteAll')} ({filteredResults.length})
                      </Button>
                    </AlertDialogTrigger>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>{t('tooltips.deleteAll')}</p>
                  </TooltipContent>
                </Tooltip>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>{t('resultsPage.deleteAllConfirmTitle')}</AlertDialogTitle>
                    <AlertDialogDescription>
                      {t('resultsPage.deleteAllConfirmDescription', { count: filteredResults.length })}
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                    <AlertDialogAction onClick={deleteAllResults}>
                      {t('resultsPage.deleteAll')}
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
        </div>

        {/* Filters */}
        <Card className="border-0 shadow-sm">
          <CardContent className="p-4 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder={t('resultsPage.searchPlaceholder')}
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Select value={filterRead} onValueChange={(v: any) => setFilterRead(v)}>
                <SelectTrigger className="w-[180px]">
                  <Filter className="w-4 h-4 mr-2" />
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('resultsPage.filterAll')}</SelectItem>
                  <SelectItem value="unread">{t('resultsPage.filterUnread')}</SelectItem>
                  <SelectItem value="read">{t('resultsPage.filterRead')}</SelectItem>
                </SelectContent>
              </Select>
              <Button
                variant={showAdvancedFilters ? "secondary" : "outline"}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                className="shrink-0"
              >
                <ChevronDown className={cn("w-4 h-4 mr-2 transition-transform", showAdvancedFilters && "rotate-180")} />
                {t('resultsPage.advancedFilters')}
                {hasAdvancedFilters && (
                  <Badge variant="secondary" className="ml-2 h-5 px-1.5">
                    {[dateFrom, dateTo, minValue, maxValue, ...selectedCountries, ...selectedCpvCodes, ...selectedBuyers].filter(Boolean).length}
                  </Badge>
                )}
              </Button>
            </div>

            {/* Advanced Filters */}
            <Collapsible open={showAdvancedFilters}>
              <CollapsibleContent className="pt-4 border-t space-y-4">
                <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {/* Date From */}
                  <div className="space-y-2">
                    <Label className="text-sm">{t('resultsPage.publicationDateFrom')}</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !dateFrom && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateFrom ? format(dateFrom, "dd/MM/yyyy") : t('resultsPage.selectDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[100]" align="start">
                        <Calendar
                          mode="single"
                          selected={dateFrom}
                          onSelect={setDateFrom}
                          initialFocus
                          className="p-3"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Date To */}
                  <div className="space-y-2">
                    <Label className="text-sm">{t('resultsPage.publicationDateTo')}</Label>
                    <Popover modal={true}>
                      <PopoverTrigger asChild>
                        <Button
                          variant="outline"
                          className={cn(
                            "w-full justify-start text-left font-normal",
                            !dateTo && "text-muted-foreground"
                          )}
                        >
                          <CalendarIcon className="mr-2 h-4 w-4" />
                          {dateTo ? format(dateTo, "dd/MM/yyyy") : t('resultsPage.selectDate')}
                        </Button>
                      </PopoverTrigger>
                      <PopoverContent className="w-auto p-0 z-[100]" align="start">
                        <Calendar
                          mode="single"
                          selected={dateTo}
                          onSelect={setDateTo}
                          initialFocus
                          className="p-3"
                        />
                      </PopoverContent>
                    </Popover>
                  </div>

                  {/* Min Value */}
                  <div className="space-y-2">
                    <Label className="text-sm">{t('resultsPage.minValue')}</Label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="0"
                        className="pl-10"
                        value={minValue}
                        onChange={(e) => setMinValue(e.target.value)}
                      />
                    </div>
                  </div>

                  {/* Max Value */}
                  <div className="space-y-2">
                    <Label className="text-sm">{t('resultsPage.maxValue')}</Label>
                    <div className="relative">
                      <Euro className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                      <Input
                        type="number"
                        placeholder="∞"
                        className="pl-10"
                        value={maxValue}
                        onChange={(e) => setMaxValue(e.target.value)}
                      />
                    </div>
                  </div>
                </div>

                {/* Include no value option */}
                {(minValue || maxValue) && (
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="includeNoValue"
                      checked={includeNoValue}
                      onCheckedChange={(checked) => setIncludeNoValue(checked as boolean)}
                    />
                    <Label htmlFor="includeNoValue" className="text-sm cursor-pointer">
                      {t('resultsPage.includeNoValue')}
                    </Label>
                  </div>
                )}

                {/* Country Filter */}
                {availableCountries.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-2">
                      <Globe className="w-4 h-4" />
                      {t('resultsPage.countries')}
                    </Label>
                    <div className="flex flex-wrap gap-2">
                      {availableCountries.sort().map(country => (
                        <Button
                          key={country}
                          variant={selectedCountries.includes(country) ? "default" : "outline"}
                          size="sm"
                          className="h-7"
                          onClick={() => toggleCountry(country)}
                        >
                          {country}
                        </Button>
                      ))}
                    </div>
                  </div>
                )}

                {/* CPV Filter with Search */}
                <CpvSearch 
                  selectedCodes={selectedCpvCodes}
                  onCodesChange={setSelectedCpvCodes}
                  availableCodes={availableCpvCodes}
                />

                {/* Buyer Filter */}
                {availableBuyers.length > 0 && (
                  <div className="space-y-2">
                    <Label className="text-sm flex items-center gap-2">
                      <Building2 className="w-4 h-4" />
                      {t('resultsPage.buyer')}
                      {selectedBuyers.length > 0 && (
                        <Badge variant="secondary" className="ml-1">
                          {selectedBuyers.length} {t('resultsPage.buyersSelected')}
                        </Badge>
                      )}
                    </Label>
                    <Input
                      placeholder={t('resultsPage.searchBuyer')}
                      value={buyerSearchQuery}
                      onChange={(e) => setBuyerSearchQuery(e.target.value)}
                      className="max-w-md"
                    />
                    <div className="flex flex-wrap gap-2 max-h-32 overflow-y-auto">
                      {/* Show selected buyers first */}
                      {selectedBuyers.map(buyer => (
                        <Button
                          key={buyer}
                          variant="default"
                          size="sm"
                          className="h-7 text-xs max-w-[200px]"
                          onClick={() => toggleBuyer(buyer)}
                        >
                          <span className="truncate">{buyer}</span>
                          <X className="w-3 h-3 ml-1 flex-shrink-0" />
                        </Button>
                      ))}
                      {/* Show filtered buyers (excluding already selected) */}
                      {filteredBuyers
                        .filter(buyer => !selectedBuyers.includes(buyer))
                        .map(buyer => (
                          <Button
                            key={buyer}
                            variant="outline"
                            size="sm"
                            className="h-7 text-xs max-w-[200px]"
                            onClick={() => toggleBuyer(buyer)}
                          >
                            <span className="truncate">{buyer}</span>
                          </Button>
                        ))}
                      {availableBuyers.length > 15 && !buyerSearchQuery && (
                        <span className="text-xs text-muted-foreground self-center">
                          +{availableBuyers.length - 15} {t('resultsPage.othersUseSearch')}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {hasAdvancedFilters && (
                  <div className="flex justify-end">
                    <Button variant="ghost" size="sm" onClick={clearAdvancedFilters}>
                      <X className="w-4 h-4 mr-2" />
                      {t('resultsPage.clearFilters')}
                    </Button>
                  </div>
                )}
              </CollapsibleContent>
            </Collapsible>
          </CardContent>
        </Card>

        {/* Free tier upgrade banner */}
        {hasMoreResults && (
          <Alert className="border-primary/30 bg-gradient-to-r from-primary/5 to-primary/10">
            <Lock className="h-4 w-4 text-primary" />
            <AlertDescription className="flex items-center justify-between">
              <span>
                {t('resultsPage.freeUserLimitMessage', { 
                  count: FREE_RESULTS_LIMIT
                })}
              </span>
              <Button 
                size="sm" 
                onClick={() => navigate('/pricing')}
                className="ml-4"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('common.upgrade', { defaultValue: 'Upgrade' })}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Results List */}
        {loading ? (
          <div className="space-y-4">
            {[1, 2, 3, 4, 5].map((i) => (
              <Skeleton key={i} className="h-32 w-full" />
            ))}
          </div>
        ) : displayedResults.length === 0 ? (
          <Card className="border-0 shadow-sm">
            <CardContent className="py-16 text-center">
              <Eye className="w-16 h-16 text-muted-foreground/30 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-muted-foreground mb-2">
                {t('resultsPage.noResults')}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t('resultsPage.noResultsDescription')}
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-4">
            {displayedResults.length > 0 && (
              <div className="flex items-center gap-2 px-2">
                <Checkbox
                  checked={selectedIds.size === displayedResults.length && displayedResults.length > 0}
                  onCheckedChange={toggleSelectAll}
                />
                <span className="text-sm text-muted-foreground">
                  {t('dataAnalysis.selectAll', 'Select all')} ({displayedResults.length})
                </span>
              </div>
            )}
            {displayedResults.map((result) => (
              <Card 
                key={result.id} 
                className={cn(
                  "border-0 shadow-sm hover:shadow-md transition-all",
                  !result.is_read && "ring-2 ring-primary/20",
                  selectedIds.has(result.id) && "ring-2 ring-primary"
                )}
              >
                <CardContent className="p-6">
                  <div className="flex items-start gap-4">
                    <Checkbox
                      checked={selectedIds.has(result.id)}
                      onCheckedChange={() => toggleSelect(result.id)}
                      className="mt-1"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        {!result.is_read && (
                          <Badge className="bg-primary">{t('resultsPage.new')}</Badge>
                        )}
                        {result.country && (
                          <Badge variant="outline">{result.country}</Badge>
                        )}
                        <span className="text-sm text-muted-foreground font-mono">
                          {result.ted_notice_id}
                        </span>
                      </div>
                      
                      <h3 className="font-semibold text-foreground text-lg mb-2 line-clamp-2">
                        {result.title}
                      </h3>
                      
                      {result.description && (
                        <p className="text-sm text-muted-foreground mb-4 line-clamp-2">
                          {result.description}
                        </p>
                      )}
                      
                      <div className="flex flex-wrap items-center gap-4 text-sm">
                        {result.buyer_name && (
                          <span className="text-muted-foreground">
                            <strong>{t('resultsPage.entity')}:</strong> {result.buyer_name}
                          </span>
                        )}
                        {result.estimated_value && (
                          <span className="flex items-center gap-1 font-medium text-primary">
                            <Euro className="w-4 h-4" />
                            {result.estimated_value.toLocaleString()} {result.currency}
                          </span>
                        )}
                        {result.deadline_date && (
                          <span className="flex items-center gap-1 text-destructive" title={formatDateLocalized(result.deadline_date, { includeTime: true })}>
                            <Clock className="w-4 h-4" />
                            {t('resultsPage.expires')}: {formatDateLocalized(result.deadline_date)} ({formatRelativeTime(result.deadline_date)})
                          </span>
                        )}
                      </div>

                      {result.cpv_codes && result.cpv_codes.length > 0 && (
                        <div className="flex flex-wrap gap-1 mt-3">
                          {result.cpv_codes.slice(0, 4).map((cpv) => (
                            <CpvBadge key={cpv} code={cpv} />
                          ))}
                          {result.cpv_codes.length > 4 && (
                            <Badge variant="secondary" className="text-xs">
                              +{result.cpv_codes.length - 4}
                            </Badge>
                          )}
                        </div>
                      )}
                    </div>

                    <div className="flex flex-col gap-2 shrink-0">
                      {result.ted_notice_id && (
                        <Button variant="default" size="sm" asChild>
                          <a 
                            href={getTedPdfUrl(result.ted_notice_id)} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            title={t('resultsPage.downloadPdf')}
                          >
                            <FileDown className="w-4 h-4 mr-1" />
                            PDF
                          </a>
                        </Button>
                      )}
                      {!result.is_read && (
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => markAsRead(result.id)}
                        >
                          <Check className="w-4 h-4 mr-1" />
                          {t('resultsPage.read')}
                        </Button>
                      )}
                      <AlertDialog>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                          asChild
                        >
                          <AlertDialogTrigger>
                            <Trash2 className="w-4 h-4 mr-1" />
                            {t('resultsPage.delete')}
                          </AlertDialogTrigger>
                        </Button>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>{t('resultsPage.deleteConfirmTitle')}</AlertDialogTitle>
                            <AlertDialogDescription>
                              {t('resultsPage.deleteConfirmDescription')}
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>{t('common.cancel')}</AlertDialogCancel>
                            <AlertDialogAction 
                              onClick={() => deleteResult(result.id)}
                              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                            >
                              {t('resultsPage.delete')}
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      {/* Send to Analysis Dialog */}
      <Dialog open={showAnalysisDialog} onOpenChange={setShowAnalysisDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Brain className="w-5 h-5 text-primary" />
              {t('dataAnalysis.sendToAnalysis', 'Send to Analysis')}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <p className="text-sm text-muted-foreground">
              {t('dataAnalysis.sendDialogDesc', 'Select an existing dataset or create a new one to add {{count}} tenders.', { count: selectedIds.size })}
            </p>
            {datasets.length > 0 && (
              <div>
                <Label className="text-sm">{t('dataAnalysis.existingDataset', 'Existing dataset')}</Label>
                <Select value={selectedDatasetId} onValueChange={v => { setSelectedDatasetId(v); setNewDatasetName(''); }}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {datasets.map(d => (
                      <SelectItem key={d.id} value={d.id}>{d.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label className="text-sm">{t('dataAnalysis.orNewDataset', 'Or create new dataset')}</Label>
              <Input
                value={newDatasetName}
                onChange={e => { setNewDatasetName(e.target.value); if (e.target.value) setSelectedDatasetId(''); }}
                placeholder={t('dataAnalysis.newDatasetPlaceholder', 'New dataset name...')}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAnalysisDialog(false)}>{t('common.cancel')}</Button>
            <Button onClick={sendToAnalysis} disabled={sendingToAnalysis || (!selectedDatasetId && !newDatasetName.trim())}>
              {sendingToAnalysis ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Plus className="w-4 h-4 mr-2" />}
              {t('dataAnalysis.addToDataset', 'Add to Dataset')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      </TooltipProvider>
    </DashboardLayout>
  );
}
